/**
 * Wallet Scanner API Client (Orchard + UTXOs)
 *
 * Wraps the Winbit32 wallet-scanner service endpoints:
 * - POST /wallet-scanner/orchard/export-ufvk
 * - POST /wallet-scanner/orchard/scan-ufvk
 * - POST /wallet-scanner/orchard/scan-ufvk/job
 * - GET  /wallet-scanner/orchard/scan-ufvk/job/{jobId}
 * - DELETE /wallet-scanner/orchard/scan-ufvk/job/{jobId}
 * - POST /wallet-scanner/utxos
 * - POST /wallet-scanner/transactions/broadcast
 *
 * Network-privacy note (Nym × Zcash / Ironwood): this client talks to our
 * HTTPS API only — never to lightwalletd. Client IPs therefore are not
 * visible at lwd; see project_outline.md → "Network privacy vs Nym…".
 * The API operator still sees the client IP and the UFVK for scan jobs.
 */

export interface WalletScannerClientConfig {
	baseUrl?: string; // default https://api.zcash.winbit32.com/api
	apiKey?: string; // optional default API Key
}

const DEFAULT_BASE_URL = 'https://api.zcash.winbit32.com/api';
const BROADCAST_TIMEOUT_MS = 120000;

/**
 * Host apps (Winbit32) wrap window.fetch with a gRPC-web interceptor;
 * broadcasts must bypass it. Embedders without an interceptor just get
 * plain fetch.
 */
const getRawFetch = (): typeof fetch => {
	if (typeof window === 'undefined') return fetch;
	const windowAny = window as any;
	return windowAny.__WB32_FETCH_ORIGINAL__ || windowAny.__WB32_NATIVE_FETCH__ || windowAny.fetch || fetch;
};

async function handleJsonResponse<T>(res: Response): Promise<T> {
	let payload: any = null;
	try {
		payload = await res.json();
	} catch (e) {
		throw new Error(`Invalid JSON response (status ${res.status})`);
	}
	if (!res.ok) {
		const msg = payload?.error || payload?.message || `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return payload as T;
}

export const createWalletScannerClient = (config: WalletScannerClientConfig = {}) => {
	const baseUrl = (config.baseUrl || DEFAULT_BASE_URL).replace(/\/$/, '');

	const withHeaders = (extra?: Record<string, string>) => {
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (config.apiKey) headers['X-API-Key'] = config.apiKey;
		if (extra) Object.assign(headers, extra);
		return headers;
	};

	return {
		// Prefer local derivation; this remote export is here for completeness
		exportUfvkRemote: async (body: {
			mnemonic: string;
			purpose?: number;
			coinType?: number;
			account?: number;
			change?: number;
			addressIndex?: number;
		}) => {
			const res = await fetch(`${baseUrl}/wallet-scanner/orchard/export-ufvk`, {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify(body)
			});
			return handleJsonResponse<{ success: boolean; data?: { ufvk: string }; error?: string }>(res);
		},

		// Immediate scan (if supported). Returns decrypted note metadata.
		scanUfvkImmediate: async (body: { ufvk: string; birthdayHeight?: number }) => {
			const res = await fetch(`${baseUrl}/wallet-scanner/orchard/scan-ufvk`, {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify(body)
			});
			return handleJsonResponse<{
				success: boolean;
				data?: { notes: Array<{ tx_hash: string; block_height: number; value: number; nullifier: string; commitment: string; ephemeral_key: string }> };
				error?: string;
			}>(res);
		},

		// Start async scan job. Returns jobId and per-job token.
		// Non-blocking: the POST returns immediately even with autoDetect:true
		// (the server resolves the birthday in the background, surfaced via
		// progress.phase 'detecting-birthday'). Poll getOrchardScanJob until
		// job.status === 'succeeded'.
		startOrchardScanJob: async (body: { ufvk: string; birthdayHeight?: number; endHeight?: number; witness?: boolean; autoDetect?: boolean; forceRescan?: boolean }, opts?: { apiKey?: string }) => {
			const res = await fetch(`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job`, {
				method: 'POST',
				headers: withHeaders(opts?.apiKey ? { 'X-API-Key': opts.apiKey } : undefined),
				body: JSON.stringify(body)
			});
			return handleJsonResponse<{ success: boolean; data?: { jobId: string; jobToken: string }; error?: string }>(res);
		},

		// Poll job status using API key or per-job token
		getOrchardScanJob: async (jobId: string, auth: { apiKey?: string; jobToken?: string }) => {
			const headers: Record<string, string> = withHeaders();
			if (auth.jobToken) headers['X-Job-Token'] = auth.jobToken;
			if (auth.apiKey) headers['X-API-Key'] = auth.apiKey;
			const res = await fetch(`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job/${jobId}`, {
				method: 'GET',
				headers
			});
			return handleJsonResponse<{
				success: boolean;
				data?: { job: { status: string; progress?: any; results?: { notes: any[] }; createdAt?: string; startedAt?: string; finishedAt?: string } };
			}>(res);
		},

		// Cancel a running job
		cancelOrchardScanJob: async (jobId: string, auth: { apiKey?: string; jobToken?: string }) => {
			const headers: Record<string, string> = withHeaders();
			if (auth.jobToken) headers['X-Job-Token'] = auth.jobToken;
			if (auth.apiKey) headers['X-API-Key'] = auth.apiKey;
			const res = await fetch(`${baseUrl}/wallet-scanner/orchard/scan-ufvk/job/${jobId}`, {
				method: 'DELETE',
				headers
			});
			return handleJsonResponse<{ success: boolean; data?: { cancelled: boolean } }>(res);
		},

		// Normalised UTXOs for provided address (transparent subset for unified)
		getUtxos: async (address: string) => {
			const res = await fetch(`${baseUrl}/wallet-scanner/utxos`, {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify({ address })
			});
			return handleJsonResponse<{
				success: boolean;
				data?: { address: string; type: string; source: string; utxoCount: number; utxos: any[]; durationMs: number; currentBlockHeight?: number };
			}>(res);
		},

		// Broadcast a signed transparent transaction hex blob.
		// Uses native fetch directly to bypass any gRPC-web fetch interceptor,
		// and an AbortController for proper timeout handling.
		broadcastRawTransaction: async (body: { rawTxHex: string; allowHighFees?: boolean }) => {
			const url = `${baseUrl}/wallet-scanner/transactions/broadcast`;
			const nativeFetch = getRawFetch();
			const controller = new AbortController();
			const timer = setTimeout(() => controller.abort(), BROADCAST_TIMEOUT_MS);

			const init: RequestInit = {
				method: 'POST',
				headers: withHeaders(),
				body: JSON.stringify(body),
				signal: controller.signal
			};

			try {
				const res = await nativeFetch(url, init);
				return await handleJsonResponse<{
					success: boolean;
					data?: { txid: string };
					error?: string;
				}>(res);
			} catch (error: any) {
				if (error?.name === 'AbortError') {
					throw new Error(`Broadcast request timed out after ${BROADCAST_TIMEOUT_MS / 1000}s`);
				}
				throw error;
			} finally {
				clearTimeout(timer);
			}
		}
	};
};

export type WalletScannerClientType = ReturnType<typeof createWalletScannerClient>;
